import { useId } from 'react'
import {
  WORDMARK_ARCS,
  WORDMARK_ARC_STROKE,
  WORDMARK_LETTERS,
  WORDMARK_LETTER_STROKE,
  WORDMARK_RING,
  WORDMARK_VIEWBOX,
} from './wordmarkGeometry'

/**
 * **The wordmark, drawn — the same lettering the app icon carries, not a second setting of the
 * same name (D201).**
 *
 * **Two surfaces say the product's name and both draw it from here.** Home's app-bar title and
 * the gate-opening cloud's lettering were both the string `SeloGate` set in Oxanium at 300, which
 * was always a stand-in: the icon on the resident's home screen carries its own drawn wordmark, so
 * three surfaces named one product in two hands. This is the icon's hand, and **retiring the
 * stand-in on both surfaces is what let the second face go** — `@font-face`, `.woff2`, OFL and all
 * (D201 (g)). Do not reintroduce a font here to "match" one of them to the other; they are one
 * drawing, differing only in the two colour properties their containers set.
 *
 * **The geometry is `wordmarkGeometry.ts`'s**, verbatim from the master and split into its own
 * `.ts` for the reason that file states. This one is the two ways of putting it on a page, and the
 * one thing it adds to the artwork is the master's own `scale(1,-1)`: the letterforms are drawn
 * y-up, so without the flip every ascender renders below the baseline and the name is upside down.
 * The ring and the two arcs sit outside that flip in the master and sit outside it here.
 *
 * **Two colours, and they are two for a reason.** The letterforms take `currentColor` and the
 * ring "o" and its flanking arcs take `--wordmark-accent` — the one thing that distinguishes this
 * wordmark from the name typed out. Flattening the ring into `currentColor` would render the mark
 * as plain lettering with a round `o`, which is the version this decision exists to replace.
 *
 * **Neither value is stated here, and that is what makes one drawing serve two grounds.** A
 * container sets the pair: the app bar sets `--charcoal` and the mark's per-scheme accent from
 * `scripts/brand-palette.mjs`'s `LIGHT.accent`/`DARK.accent`, so it agrees with the hamburger and
 * the view control beside it; the opening cloud sets `--cloud-ink` and the **light** accent in
 * *both* schemes, because a lit cream cloud is the ground in both — the same argument `--cloud-ink`
 * itself already makes, and the one `brand/README.md` makes for the tab-bar fallbacks.
 *
 * **The colours are classes rather than presentation attributes.** `fill="var(--x)"` in a
 * presentation attribute is legal CSS and has patchy support across the browsers this ships to;
 * a class carries no such question, and it keeps every colour in the stylesheet, which is where
 * the per-scheme rule already lives. Nothing here names a hex, so there is no stale palette value
 * under `web/src` to drift from the master.
 */

/**
 * **The drawing itself, with no box of its own.** Four paths in the master's coordinates and
 * nothing else — no `<svg>`, no `viewBox`, no size, no colour. Both consumers wrap it: the app bar
 * in an `<svg>` sized from the stylesheet, the opening cloud (`GateOpenOverlay.tsx`) in a `<g>`
 * transformed into the scene's own 390x760 space. **Neither holds a copy of the geometry**, which
 * is the whole point: the day `brand/selogate-mark.svg` is redrawn, one file changes.
 *
 * The three classes are the colour seams. They carry no `fill`/`stroke` here, deliberately —
 * `global.css` paints `.wm-letters` from `currentColor` and the other two from
 * `--wordmark-accent`, so a *container* sets two properties and the mark follows. That is what
 * lets one drawing be charcoal-on-cream chrome in the bar and near-black-on-lit-cloud in the
 * overlay without a second set of paint rules.
 */
export function WordmarkPaths(): JSX.Element {
  return (
    <>
      {/* The master's own flip: the letterforms are drawn y-up. */}
      <g transform="scale(1,-1)">
        <path
          className="wm-letters"
          d={WORDMARK_LETTERS}
          strokeWidth={WORDMARK_LETTER_STROKE}
          strokeLinejoin="round"
        />
      </g>
      <path className="wm-accent" d={WORDMARK_RING} fillRule="evenodd" />
      {WORDMARK_ARCS.map((d) => (
        <path key={d} className="wm-arc" d={d} strokeWidth={WORDMARK_ARC_STROKE} strokeLinecap="round" />
      ))}
    </>
  )
}

/**
 * **The standalone mark, for a caller that wants an element rather than SVG children** — today
 * Home's app bar, through `AppBar.tsx`'s `wordmark` prop.
 *
 * **It states no `width`/`height`**, so `global.css` owns the box: the height follows
 * `--font-appBar` and the width follows the aspect ratio, which is what lets it letterbox on a
 * narrow phone instead of ellipsising the product's name. `infra/tests/svg-geometry.test.ts`
 * registers this file for that reason — it authors geometry attributes, and a `viewBox` that
 * deferred to the cascade would be dropped whole by an engine older than Chrome 139.
 *
 * **It names itself.** The title it replaced was text, so it had an accessible name for free; an
 * `<svg>` has none. `role="img"` plus a referenced `<title>` gives it one back —
 * `aria-labelledby` rather than `aria-label` because the string is already translated by the
 * caller and a `<title>` is what a non-AT reader (a tooltip, a saved file) gets as well. On Home's
 * interactive bar the `<button>` carries its own `aria-label` and wins; this is what announces the
 * mark in reorder mode, where the title is an inert `<span>`.
 */
export function Wordmark({ label }: { readonly label: string }): JSX.Element {
  const titleId = useId()
  return (
    <svg
      className="wordmark"
      viewBox={WORDMARK_VIEWBOX}
      role="img"
      aria-labelledby={titleId}
      focusable="false"
    >
      <title id={titleId}>{label}</title>
      <WordmarkPaths />
    </svg>
  )
}
